import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** Frequency captured by the form. `count` is only set for 'Exact Number'. */
export interface LogBugFrequency {
  type:  'Always' | 'Sometimes' | 'Exact Number';
  count: number | null;
}

/** What the widget collects before it's turned into multipart form-data. */
export interface LogBugPayload {
  module:      string;
  description: string;
  severity:    'High' | 'Medium' | 'Low';
  frequency:   LogBugFrequency;
  planId:      string;
  attachments: File[];
}

/** Emitted while a submission is in flight. */
export type LogBugProgress =
  | { kind: 'progress'; percent: number }
  | { kind: 'done' };

@Injectable({ providedIn: 'root' })
export class LogBugService {
  private readonly url = `${environment.apiBaseUrl}/log-bug`;

  constructor(private http: HttpClient) {}

  /**
   * Submits a bug report as multipart/form-data and reports upload progress so
   * the UI can show a progress bar for attachments. The Bearer token (user
   * email) is attached automatically by AuthInterceptor.
   *
   * userName/email are NOT sent as trusted fields — the backend derives them
   * from the session. We pass the page URL as a future-proofing hint.
   */
  submit(payload: LogBugPayload): Observable<LogBugProgress> {
    const fd = new FormData();
    fd.append('module', payload.module);
    fd.append('description', payload.description);
    fd.append('severity', payload.severity);
    fd.append('frequencyType', payload.frequency.type);
    if (payload.frequency.type === 'Exact Number' && payload.frequency.count != null) {
      fd.append('frequencyCount', String(payload.frequency.count));
    }
    fd.append('planId', payload.planId ?? '');
    fd.append('url', window.location.href);

    for (const file of payload.attachments) {
      fd.append('attachments', file, file.name);
    }

    return this.http
      .post(this.url, fd, { reportProgress: true, observe: 'events' })
      .pipe(map((event: HttpEvent<unknown>): LogBugProgress => {
        if (event.type === HttpEventType.UploadProgress) {
          const percent = event.total ? Math.round((event.loaded / event.total) * 100) : 0;
          return { kind: 'progress', percent };
        }
        if (event.type === HttpEventType.Response) {
          return { kind: 'done' };
        }
        // Other events (Sent, ResponseHeader, …) — treat as 0% / no-op progress.
        return { kind: 'progress', percent: 0 };
      }));
  }
}
